<template>
  <v-container fluid>

    <h2 class="text-h5 font-weight-bold mb-6 page-title">📚 {{ $t('agreement.title') }}</h2>

    <v-alert v-if="message" :type="messageType" dense border="left" class="mb-4">
      {{ message }}
    </v-alert>

    <!-- LISTADO DOCUMENTOS -->
    <v-row dense>
      <v-col v-for="documentItem in documents" :key="documentItem.id" cols="12" md="6" lg="4">
        <v-card class="pa-4 document-card" outlined>

          <div class="d-flex align-center justify-space-between mb-3 document-header">
            <div class="d-flex align-center document-heading">
              <v-avatar color="primary" size="44" class="mr-3">
                <v-icon color="white">mdi-file-document-outline</v-icon>
              </v-avatar>

              <h3 class="text-subtitle-1 font-weight-bold mb-0">
                {{ documentItem.title }}
              </h3>
            </div>

            <v-menu bottom right>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click="openEditDocument(documentItem)">
                  <v-list-item-title>
                    <v-icon small class="pr-2">mdi-pencil</v-icon>{{ $t('common.edit') }}
                  </v-list-item-title>
                </v-list-item>

                <v-list-item @click="deleteDocument(documentItem)">
                  <v-list-item-title>
                    <v-icon small color="red" class="pr-2">mdi-delete</v-icon>{{ $t('common.delete') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <!-- DESCRIPCIÓN -->
          <div class="text-body-2 mb-3">
            {{ documentItem.description }}
          </div>

          <v-divider></v-divider>

          <!-- BOTONES VER -->
          <v-btn v-if="documentItem.type !== 'text'" color="primary" text small @click="openPdf(documentItem)">
            <v-icon left small>mdi-file-eye</v-icon>
            {{ $t('agreement.viewDocument') }}
          </v-btn>

          <v-btn v-if="documentItem.type === 'text'" color="primary" text small @click="openText(documentItem)">
            <v-icon left small>mdi-text-box-outline</v-icon>
            {{ $t('agreement.viewText') }}
          </v-btn>

        </v-card>
      </v-col>
    </v-row>

    <!-- BOTÓN NUEVO -->
    <v-btn color="primary" fab dark fixed bottom right class="elevation-6" data-tour="agreement-create" @click="openAddModal">
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <!-- 🔹 MODAL CREAR / EDITAR DOCUMENTO (scroll body + actions fijas) -->
    <v-dialog v-model="dialog" max-width="920px" height="1000px">
      <v-card class="dialog-scroll">

        <v-card-title class="text-h6 font-weight-bold">
          {{ editing ? $t('agreement.editDocument') : $t('agreement.newDocument') }}
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="documentForm" v-model="documentValid">

          <v-text-field
            v-model="form.title"
            :label="$t('agreement.documentTitle')"
            outlined
            dense
            hide-details="auto"
            class="compact-field"
            :rules="[rules.required]"
            required
          />

          <v-text-field
            v-model="form.description"
            :label="$t('agreement.description')"
            outlined
            dense
            class="compact-field"
            counter="500"
            maxlength="500"
            :error="form.description.length > 500"
            :error-messages="form.description.length > 500 ? [$t('agreement.maxDescription')] : []"
          />

          <!-- SELECCIÓN TIPO -->
          <v-select
            v-model="form.type"
            :items="[
              { text: $t('agreement.editableText'), value: 'text' },
              { text: $t('agreement.pdfDocument'), value: 'pdf' }
            ]"
            :label="$t('agreement.documentType')"
            outlined
            dense
            hide-details="auto"
            class="compact-field"
            :rules="[rules.required]"
            required
          />

          <!-- EDITOR TIPO TEXTO -->
          <div v-if="form.type === 'text'" class="mt-2">
            <tiptap-vuetify ref="editorRef" v-model="form.content" />
          </div>

          <!-- INFO DOCUMENTO ACTUAL (SI EXISTE) -->
          <div
            v-if="editing && form.type !== 'text' && form.fileUrl"
            class="mt-4 mb-2 d-flex align-center current-file"
          >
            <span class="mr-2 text-caption">{{ $t('agreement.currentDocument') }}</span>

            <v-chip small class="mr-2" outlined>
              <v-icon left small>mdi-file</v-icon>
              {{ form.fileName || $t('common.document') }}
            </v-chip>

            <v-btn small text color="primary" @click="openPdf(form)">
              <v-icon left small>mdi-file-eye</v-icon>
              {{ $t('common.view') }}
            </v-btn>
          </div>

          <!-- SUBIR ARCHIVO (OPCIONAL AL EDITAR) -->
          <v-file-input
            v-if="form.type !== 'text'"
            v-model="file"
            accept=".pdf,.doc,.docx"
            :label="editing && form.fileUrl ? $t('agreement.replaceFile') : $t('agreement.uploadFile')"
            outlined
            dense
            class="mt-2"
            :rules="[rules.fileRequired]"
          />
          </v-form>

        </v-card-text>

        <v-card-actions class="justify-end fixed-actions">
          <v-btn text @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveDocument">
            {{ editing ? $t('agreement.saveChanges') : $t('agreement.createDocument') }}
          </v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>

    <!-- 🔹 DIÁLOGO DE CONFIRMACIÓN -->
    <v-dialog v-model="dialogConfirm" width="320px">
      <v-card class="dialog-scroll">
        <v-card-title class="text-h6 font-weight-bold">{{ $t('common.confirmAction') }}</v-card-title>

        <v-card-text class="dialog-content">
          {{ confirmMessage }}
        </v-card-text>

        <v-card-actions class="justify-end fixed-actions">
          <v-btn text @click="dialogConfirm = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="red" dark @click="confirmAction(); dialogConfirm = false">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { db, storage } from "@/firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  query
} from "firebase/firestore";
import { ref as fbRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { sendCompanyPushNotification } from "@/services/pushNotifications";
import { getUserErrorMessage } from "@/utils/errors";

import TiptapVuetify from "@/components/editor/TiptapVuetify.vue";

export default {
  name: "Agreement",
  components: { TiptapVuetify },

  computed: {
    companyId() {
      return this.$store.getters["auth/companyId"];
    },
    user() {
      return this.$store.getters["auth/user"];
    },
    rules() {
      return {
        required: (value) => !!value || this.$t("common.required"),
        fileRequired: (value) => (
          this.form.type === "text" || !!this.form.fileUrl || !!value
        ) || this.$t("common.required"),
      };
    },
  },

  data() {
    return {
      documents: [],
      message: "",
      messageType: "success",

      dialog: false,
      dialogConfirm: false,

      editing: false,
      file: null,
      documentValid: false,

      confirmMessage: "",
      confirmAction: null,

      form: {
        id: null,
        title: "",
        description: "",
        type: "text",
        content: "",
        fileUrl: "",
        fileName: "",
      },
    };
  },

  async created() {
    await this.loadDocuments();
  },

  methods: {
    async loadDocuments() {
      try {
        this.message = "";
        const refDocs = collection(db, "companies", this.companyId, "documents");
        const qDocs = query(refDocs, orderBy("createdAt", "desc"));
        const snap = await getDocs(qDocs);
        this.documents = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "agreement.loadError", this.$i18n), "error");
      }
    },

    closeDialog() {
      this.dialog = false;

      this.form = {
        id: null,
        title: "",
        description: "",
        type: "text",
        content: "",
        fileUrl: "",
        fileName: "",
      };

      this.file = null;
      this.editing = false;
      this.$nextTick(() => this.$refs.documentForm?.resetValidation());
    },

    /* -----------------------------
       CONFIRM DIALOG
    ------------------------------ */
    openConfirm(message, action) {
      this.confirmMessage = message;
      this.confirmAction = action;
      this.dialogConfirm = true;
    },

    /* -----------------------------
       DOCUMENTOS
    ------------------------------ */
    openAddModal() {
      this.editing = false;
      this.dialog = true;
      this.resetForm();
    },

    openEditDocument(documentItem) {
      // Limpiamos primero
      this.resetForm();

      // Rellenamos con el documento actual
      this.form = {
        id: documentItem.id,
        title: documentItem.title || "",
        description: documentItem.description || "",
        type: documentItem.type || "text",
        content: documentItem.content || "",
        fileUrl: documentItem.fileUrl || "",
        fileName: documentItem.fileName || "",
      };

      this.file = null; // importante para no arrastrar el archivo anterior del input
      this.editing = true;
      this.dialog = true;
    },

    resetForm() {
      this.form = {
        id: null,
        title: "",
        description: "",
        type: "text",
        content: "",
        fileUrl: "",
        fileName: "",
      };
      this.file = null;
      this.$nextTick(() => this.$refs.documentForm?.resetValidation());
    },

    async saveDocument() {
      if (!this.$refs.documentForm.validate()) return;
      if (this.form.description.length > 500) return;

      try {
        let fileUrl = this.form.fileUrl;
        let fileName = this.form.fileName;

        // Si no es texto y se ha subido un nuevo archivo, reemplazamos
        if (this.form.type !== "text" && this.file) {
          const fileRef = fbRef(
            storage,
            `companies/${this.companyId}/documents/${Date.now()}_${this.file.name}`
          );

          await uploadBytes(fileRef, this.file);
          fileUrl = await getDownloadURL(fileRef);
          fileName = this.file.name;
        }

        /* EDITAR */
        if (this.editing) {
          const docRef = doc(
            db,
            "companies",
            this.companyId,
            "documents",
            this.form.id
          );

          await updateDoc(docRef, {
            title: this.form.title,
            description: this.form.description,
            type: this.form.type,
            content: this.form.type === "text" ? this.form.content : "",
            fileUrl,
            fileName,
          });

          // 🔥 actualizar localmente
          const index = this.documents.findIndex((d) => d.id === this.form.id);
          if (index !== -1) {
            this.documents.splice(index, 1, {
              id: this.form.id,
              title: this.form.title,
              description: this.form.description,
              type: this.form.type,
              content: this.form.type === "text" ? this.form.content : "",
              fileUrl,
              fileName,
            });
          }
        }
        else {
          /* CREAR */
          const ref = await addDoc(
            collection(db, "companies", this.companyId, "documents"),
            {
              title: this.form.title,
              description: this.form.description,
              type: this.form.type,
              content: this.form.type === "text" ? this.form.content : "",
              fileUrl,
              fileName,
              createdByUid: this.user?.uid || "",
              createdByEmail: this.user?.email || "",
              createdAt: new Date(),
            }
          );

          // 🔥 insertar en memoria
          this.documents.unshift({
            id: ref.id,
            title: this.form.title,
            description: this.form.description,
            type: this.form.type,
            content: this.form.type === "text" ? this.form.content : "",
            fileUrl,
            fileName,
            createdByUid: this.user?.uid || "",
            createdByEmail: this.user?.email || "",
          });

          sendCompanyPushNotification(this.companyId, {
            notificationType: "agreement",
            title: this.$t("agreement.notificationTitle"),
            body: this.form.title,
            data: {
              companyId: this.companyId,
              documentId: ref.id,
            },
          }).catch((error) => {
            console.warn(this.$t("agreement.notificationError"), error);
          });
        }

        this.closeDialog();
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "agreement.saveError", this.$i18n), "error");
      }
    },

    deleteDocument(documentItem) {
      this.openConfirm(this.$t("agreement.deleteConfirm", { title: documentItem.title }), async () => {
        try {
          await deleteDoc(
            doc(db, "companies", this.companyId, "documents", documentItem.id)
          );

          this.documents = this.documents.filter(d => d.id !== documentItem.id);
        } catch (error) {
          this.showMessage(getUserErrorMessage(error, "agreement.deleteError", this.$i18n), "error");
        }
      });
    },

    /* -----------------------------
       VER DOCUMENTOS
    ------------------------------ */
    openPdf(documentItem) {
      if (!documentItem.fileUrl) return;
      window.open(documentItem.fileUrl, "_blank");
    },

    openText(documentItem) {
      const newWindow = window.open("", "_blank");
      newWindow.document.write(`
        <html>
        <head>
          <title>${documentItem.title}</title>
          <style>
            body { font-family: Arial; padding: 24px; line-height: 1.6; }
            blockquote { border-left: 4px solid #ccc; margin: 16px 0; padding-left: 12px; }
            h1,h2,h3 { margin-top: 24px; }
          </style>
        </head>
        <body>${documentItem.content}</body>
        </html>
      `);
      newWindow.document.close();
    },

    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
    },
  },
};
</script>

<style scoped>
.document-card {
  background-color: #f8f7fd;
  border-radius: 12px;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.document-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
}

.v-btn.fixed {
  position: fixed !important;
  bottom: 24px !important;
  right: 24px !important;
}

/* --------- DIALOGOS CON SCROLL --------- */

.dialog-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-bottom: 16px;
}

.compact-field {
  margin-bottom: 8px !important;
}

.compact-field ::v-deep .v-text-field__details {
  min-height: 16px;
  margin-bottom: 0;
  padding-top: 0;
}

/* acciones fijas abajo */
.fixed-actions {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #eee;
  z-index: 5;
}

/* bloque del documento actual */
.current-file .v-chip {
  cursor: default;
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1.2rem !important;
    line-height: 1.3;
    margin-bottom: 16px !important;
  }

  .document-card {
    border-radius: 10px;
    min-height: 0;
  }

  .document-header {
    align-items: flex-start !important;
    gap: 8px;
  }

  .document-heading {
    min-width: 0;
  }

  .document-heading h3 {
    overflow-wrap: anywhere;
  }

  .v-btn.fixed {
    bottom: 18px !important;
    right: 18px !important;
  }

  ::v-deep .v-dialog {
    margin: 12px;
  }

  .dialog-scroll {
    max-height: calc(100vh - 24px);
  }

  .current-file {
    align-items: flex-start !important;
    flex-direction: column;
    gap: 8px;
  }

  .fixed-actions {
    flex-wrap: wrap;
    gap: 8px;
  }
}

</style>
